import { useState, useEffect, useRef } from "react";

/* ─────────────────────────────────────────────
   STYLES
───────────────────────────────────────────── */
const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Epilogue:wght@400;500;600;700;800;900&family=Inter:wght@300;400;500;600;700&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
html { scroll-behavior: smooth; }
body {
  font-family: 'Inter', sans-serif;
  color: #111;
  background: #fff;
  -webkit-font-smoothing: antialiased;
  overflow-x: hidden;
}
h1,h2,h3,h4,h5,h6 { font-family: 'Epilogue', sans-serif; }
a { text-decoration: none; color: inherit; }
button { cursor: pointer; font-family: 'Inter', sans-serif; }
img { max-width: 100%; display: block; }

/* ── PORTFOLIO HERO ── */
.portfolio-hero {
  background: #0a0e27;
  padding: 90px 48px 90px;
  position: relative; overflow: hidden;
}
.portfolio-hero::before {
  content: '';
  position: absolute; inset: 0;
  background:
    radial-gradient(ellipse 60% 50% at 80% 50%, rgba(255,80,0,0.10) 0%, transparent 70%),
    radial-gradient(ellipse 40% 60% at 10% 80%, rgba(30,60,180,0.18) 0%, transparent 60%);
  pointer-events: none;
}
.portfolio-hero-in { max-width: 1200px; margin: 0 auto; position: relative; z-index: 1; }
.portfolio-hero-eyebrow {
  font-size: 11px; font-weight: 700; letter-spacing: 0.14em;
  text-transform: uppercase; color: #ff5000;
  display: inline-flex; align-items: center; gap: 8px;
  margin-bottom: 20px;
  opacity: 0; transform: translateY(20px);
  animation: hIn 0.7s cubic-bezier(0.22,1,0.36,1) 0.1s forwards;
}
.portfolio-hero-eyebrow::before {
  content: ''; width: 24px; height: 2px; background: #ff5000; border-radius: 2px;
}
.portfolio-hero-h1 {
  font-size: clamp(40px, 5.5vw, 76px); font-weight: 900;
  color: #fff; letter-spacing: -2.5px; line-height: 1.0;
  max-width: 700px; margin-bottom: 24px;
  opacity: 0; transform: translateY(52px);
  animation: hIn 1.0s cubic-bezier(0.22,1,0.36,1) 0.18s forwards;
}
.portfolio-hero-h1 em { font-style: normal; color: #ff5000; }
.portfolio-hero-sub {
  font-size: 17px; line-height: 1.75; color: rgba(255,255,255,0.5);
  max-width: 480px; margin-bottom: 48px;
  opacity: 0; transform: translateY(28px);
  animation: hIn 0.8s cubic-bezier(0.22,1,0.36,1) 0.32s forwards;
}
.portfolio-hero-stats {
  display: flex; gap: 48px; flex-wrap: wrap;
  opacity: 0;
  animation: hIn 0.8s cubic-bezier(0.22,1,0.36,1) 0.46s forwards;
}
.pf-stat-num {
  font-family: 'Epilogue', sans-serif; font-size: 38px; font-weight: 900;
  color: #fff; letter-spacing: -1.5px; line-height: 1;
}
.pf-stat-num span { color: #ff5000; }
.pf-stat-lbl { font-size: 12px; color: rgba(255,255,255,0.4); margin-top: 4px; font-weight: 500; }

@keyframes hIn { to { opacity: 1; transform: none; } }

/* ── FILTER BAR ── */
.pf-filter-wrap {
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
  position: sticky; top: 0; z-index: 50;
}
.pf-filter-in {
  max-width: 1200px; margin: 0 auto; padding: 0 48px;
  display: flex; align-items: center; justify-content: space-between;
  gap: 16px; flex-wrap: wrap;
}
.pf-filters {
  display: flex; gap: 4px;
  overflow-x: auto; -webkit-overflow-scrolling: touch; scrollbar-width: none;
  padding: 16px 0;
}
.pf-filters::-webkit-scrollbar { display: none; }
.pf-filter-btn {
  font-size: 13px; font-weight: 600;
  padding: 8px 18px; border-radius: 100px; border: 1.5px solid #e8e8e8;
  background: #fff; color: #888; white-space: nowrap;
  transition: all 0.2s;
}
.pf-filter-btn:hover { border-color: #111; color: #111; }
.pf-filter-btn.on { background: #111; border-color: #111; color: #fff; }
.pf-filter-btn.on-orange { background: #ff5000; border-color: #ff5000; color: #fff; }
.pf-count {
  font-size: 13px; color: #aaa; font-weight: 500; white-space: nowrap; padding: 16px 0;
}
.pf-count strong { color: #111; font-weight: 700; }

/* ── PROJECTS GRID ── */
.pf-grid-sec {
  background: #fafafa;
  padding: 60px 48px 100px;
}
.pf-grid-in { max-width: 1200px; margin: 0 auto; }

/* Masonry-style: 2 cols, varying heights */
.pf-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 28px;
}

/* Project card */
.pf-card {
  background: #fff;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid #eee;
  transition: transform 0.35s cubic-bezier(0.22,1,0.36,1), box-shadow 0.35s cubic-bezier(0.22,1,0.36,1);
  cursor: pointer;
}
.pf-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 24px 64px rgba(0,0,0,0.10);
}
/* Featured card spans both columns */
.pf-card.featured {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: 1fr 1fr;
}
.pf-card.featured .pf-card-img-wrap {
  aspect-ratio: unset; min-height: 360px;
}
.pf-card.featured .pf-card-body {
  padding: 48px 44px;
  display: flex; flex-direction: column; justify-content: space-between;
}

.pf-card-img-wrap {
  position: relative; overflow: hidden;
  aspect-ratio: 16/10; background: #f0f2f8;
}
.pf-card-img {
  width: 100%; height: 100%; object-fit: cover;
  transition: transform 0.6s cubic-bezier(0.22,1,0.36,1);
}
.pf-card:hover .pf-card-img { transform: scale(1.05); }

/* Overlay on hover */
.pf-card-overlay {
  position: absolute; inset: 0;
  background: linear-gradient(180deg, transparent 40%, rgba(10,14,39,0.7) 100%);
  opacity: 0; transition: opacity 0.35s;
  display: flex; align-items: flex-end; padding: 24px;
}
.pf-card:hover .pf-card-overlay { opacity: 1; }
.pf-overlay-cta {
  background: #ff5000; color: #fff; border: none;
  font-size: 13px; font-weight: 700;
  padding: 10px 20px; border-radius: 8px;
  display: flex; align-items: center; gap: 8px;
  transform: translateY(8px); opacity: 0;
  transition: transform 0.3s 0.05s, opacity 0.3s 0.05s;
}
.pf-card:hover .pf-overlay-cta { transform: none; opacity: 1; }

.pf-card-body { padding: 28px 28px 24px; }
.pf-card-tag {
  display: inline-block;
  font-size: 10px; font-weight: 800; letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 4px 10px; border-radius: 4px;
  margin-bottom: 14px;
}
.tag-vr     { background: #eef0ff; color: #4455dd; }
.tag-ar     { background: #fff0e8; color: #ff5000; }
.tag-web    { background: #e8f7ee; color: #1a9e52; }
.tag-mobile { background: #fdf3e8; color: #d97706; }
.tag-social { background: #fce8f3; color: #cc2277; }
.tag-ai     { background: #eef6ff; color: #2277cc; }
.tag-nft    { background: #f3e8ff; color: #7c3aed; }

.pf-card-title {
  font-family: 'Epilogue', sans-serif;
  font-size: 20px; font-weight: 900;
  color: #111; letter-spacing: -0.5px; line-height: 1.25;
  margin-bottom: 10px;
}
.pf-card.featured .pf-card-title {
  font-size: clamp(24px, 2.5vw, 32px); margin-bottom: 14px;
}
.pf-card-desc {
  font-size: 14px; line-height: 1.7; color: #777;
  margin-bottom: 20px;
}
.pf-card-footer {
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap; gap: 10px;
}
.pf-card-client {
  display: flex; align-items: center; gap: 8px;
}
.pf-client-dot {
  width: 28px; height: 28px; border-radius: 50%;
  background: linear-gradient(135deg, #ff5000, #ff8c40);
  display: flex; align-items: center; justify-content: center;
  font-size: 10px; font-weight: 800; color: #fff; flex-shrink: 0;
}
.pf-client-name { font-size: 12px; font-weight: 600; color: #555; }
.pf-card-link {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 13px; font-weight: 700; color: #ff5000;
  background: none; border: none; padding: 0;
  transition: gap 0.2s;
}
.pf-card-link:hover { gap: 10px; }

/* ── EMPTY STATE ── */
.pf-empty {
  grid-column: 1 / -1;
  padding: 80px 0; text-align: center;
}
.pf-empty-ico { font-size: 48px; margin-bottom: 16px; }
.pf-empty-h { font-family: 'Epilogue', sans-serif; font-size: 22px; font-weight: 800; color: #111; margin-bottom: 8px; }
.pf-empty-p { font-size: 14px; color: #888; }

/* ── CTA BAND ── */
.pf-cta {
  background: #0a0e27;
  padding: 90px 48px;
  text-align: center;
  position: relative; overflow: hidden;
}
.pf-cta::before {
  content: ''; position: absolute; inset: 0;
  background: radial-gradient(ellipse 70% 80% at 50% 100%, rgba(255,80,0,0.12) 0%, transparent 70%);
  pointer-events: none;
}
.pf-cta-in { max-width: 640px; margin: 0 auto; position: relative; z-index: 1; }
.pf-cta-ey {
  font-size: 11px; font-weight: 700; letter-spacing: 0.14em;
  text-transform: uppercase; color: #ff5000;
  display: inline-flex; align-items: center; gap: 8px;
  margin-bottom: 20px;
}
.pf-cta-ey::before { content: ''; width: 20px; height: 2px; background: #ff5000; border-radius: 2px; }
.pf-cta-h2 {
  font-size: clamp(28px, 4vw, 52px); font-weight: 900;
  color: #fff; letter-spacing: -1.5px; line-height: 1.1;
  margin-bottom: 18px;
}
.pf-cta-h2 em { font-style: normal; color: #ff5000; }
.pf-cta-p { font-size: 16px; line-height: 1.75; color: rgba(255,255,255,0.5); margin-bottom: 36px; }
.pf-cta-btns { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }
.pf-btn-fill {
  background: #ff5000; color: #fff; border: none;
  font-size: 15px; font-weight: 700;
  padding: 15px 32px; border-radius: 10px;
  transition: background 0.2s, transform 0.15s;
}
.pf-btn-fill:hover { background: #e04500; transform: translateY(-2px); }
.pf-btn-ghost {
  background: transparent; color: rgba(255,255,255,0.7);
  border: 1.5px solid rgba(255,255,255,0.2);
  font-size: 15px; font-weight: 600;
  padding: 15px 32px; border-radius: 10px;
  transition: border-color 0.2s, color 0.2s;
}
.pf-btn-ghost:hover { border-color: rgba(255,255,255,0.6); color: #fff; }

/* ── SCROLL ANIMATIONS ── */
[data-reveal] {
  will-change: opacity, transform;
  transition-property: opacity, transform;
  transition-timing-function: cubic-bezier(0.22, 1, 0.36, 1);
  transition-duration: 700ms;
}
[data-reveal="fade-up"]    { opacity: 0; transform: translateY(44px); }
[data-reveal="fade-down"]  { opacity: 0; transform: translateY(-28px); }
[data-reveal="fade-left"]  { opacity: 0; transform: translateX(52px); }
[data-reveal="fade-right"] { opacity: 0; transform: translateX(-52px); }
[data-reveal="fade-scale"] { opacity: 0; transform: scale(0.92) translateY(20px); }
[data-reveal="fade-in"]    { opacity: 0; }
[data-reveal].rv-in  { opacity: 1 !important; transform: none !important; }
[data-reveal].rv-out { transition-duration: 260ms !important; transition-timing-function: cubic-bezier(0.4,0,1,1) !important; }

/* ── RESPONSIVE ── */
@media (max-width: 900px) {
  .portfolio-hero { padding: 60px 24px 72px; }
  .pf-filter-in { padding: 0 24px; }
  .pf-grid-sec { padding: 40px 24px 72px; }
  .pf-grid { grid-template-columns: 1fr; }
  .pf-card.featured { grid-template-columns: 1fr; }
  .pf-card.featured .pf-card-img-wrap { min-height: 240px; aspect-ratio: 16/9; }
  .pf-card.featured .pf-card-body { padding: 28px; }
  .pf-cta { padding: 72px 24px; }
  .pf-ftr { padding: 24px; }
  .portfolio-hero-stats { gap: 28px; }
}
@media (max-width: 480px) {
  .portfolio-hero-h1 { letter-spacing: -1.5px; }
  .pf-grid { gap: 18px; }
}
`;

/* ─────────────────────────────────────────────
   DATA
───────────────────────────────────────────── */
const PROJECTS = [
  {
    id: 1,
    featured: true,
    tag: 'VR Training',
    tagClass: 'tag-vr',
    title: 'VR Training Module for Western Railway',
    desc: "Built India's first interactive VR training system for Western Railway's Air Conditioned suburban train fleet. Engineers use immersive walkthroughs to learn complex maintenance procedures without physical risk.",
    client: 'Western Railway',
    initials: 'WR',
    img: 'https://images.unsplash.com/photo-1617802690992-15d93263d3a9?w=900&q=80',
    cats: ['All', 'VR / AR'],
  },
  {
    id: 2,
    tag: 'Platform Building',
    tagClass: 'tag-web',
    title: 'TradeBaazar Web & Mobile App',
    desc: 'Full-stack trading platform enabling customers to trade at BSE, NSE and MCX — 18 years of bonding, now digital.',
    client: 'TradeBaazar',
    initials: 'TB',
    img: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=700&q=80',
    cats: ['All', 'Web & Mobile'],
  },
  {
    id: 3,
    tag: 'Digital Campaigns',
    tagClass: 'tag-social',
    title: 'Digital Campaigns for Paytm',
    desc: 'Influencer-led video promotion and paid social media ads driving organic engagement and measurable views at scale.',
    client: 'Paytm',
    initials: 'PT',
    img: 'https://images.unsplash.com/photo-1611926653458-09294b3142bf?w=700&q=80',
    cats: ['All', 'Digital Marketing'],
  },
  {
    id: 4,
    tag: 'Utility Apps',
    tagClass: 'tag-mobile',
    title: 'Digital Signature App',
    desc: 'Attendance automation app for ministers across Delhi, Gujarat, J&K and MP — deployed across government offices statewide.',
    client: 'Government of India',
    initials: 'GI',
    img: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=700&q=80',
    cats: ['All', 'Web & Mobile'],
  },
  {
    id: 5,
    tag: 'VR Games',
    tagClass: 'tag-vr',
    title: 'VR Game — Mercedes-Benz',
    desc: 'Interactive VR showroom where visitors configure a Mercedes — wheels, colour, doors, and lights — in real time inside dealerships.',
    client: 'Mercedes-Benz',
    initials: 'MB',
    img: 'https://images.unsplash.com/photo-1605559424843-9e4c228bf1c2?w=700&q=80',
    cats: ['All', 'VR / AR'],
  },
  {
    id: 6,
    tag: 'VR Games',
    tagClass: 'tag-vr',
    title: "VR Car Simulation — Nissan",
    desc: "India's first VR simulation experience for Nissan, deployed in malls across the country. Users explore and interact with the real car in a virtual environment.",
    client: 'Nissan India',
    initials: 'NI',
    img: 'https://images.unsplash.com/photo-1593508512255-86ab42a8e620?w=700&q=80',
    cats: ['All', 'VR / AR'],
  },
  {
    id: 7,
    tag: 'Chatbots',
    tagClass: 'tag-ai',
    title: 'SalesBot Voice & Chat — AI Platform',
    desc: 'Personalised recommendation engine with spinning wheels and special offers. Targeted re-engagement for customers who browsed products — converting intent into purchase.',
    client: 'Multiple Clients',
    initials: 'AI',
    img: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?w=700&q=80',
    cats: ['All', 'AI & Chatbots'],
  },
  {
    id: 8,
    tag: 'AR Game',
    tagClass: 'tag-ar',
    title: 'Nestle Munch AR Game',
    desc: 'Augmented reality game created for Nestlé Munch across Indian festivals and product launches — turning packaging into a playable experience.',
    client: 'Nestlé India',
    initials: 'NE',
    img: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=700&q=80',
    cats: ['All', 'VR / AR'],
  },
];

const CATS = ['All', 'VR / AR', 'Web & Mobile', 'Digital Marketing', 'AI & Chatbots'];

/* ─────────────────────────────────────────────
   SCROLL REVEAL HOOK
───────────────────────────────────────────── */
function useScrollReveal(dep) {
  useEffect(() => {
    let scrollDir = 'down';
    let lastY = window.scrollY;
    const updateDir = () => {
      const y = window.scrollY;
      if (y !== lastY) { scrollDir = y > lastY ? 'down' : 'up'; lastY = y; }
    };
    window.addEventListener('scroll', updateDir, { passive: true });

    const singles = Array.from(
      document.querySelectorAll('[data-reveal]:not([data-stagger] *)')
    );
    const staggerParents = Array.from(document.querySelectorAll('[data-stagger]'));
    const staggerChildren = [];
    staggerParents.forEach(parent => {
      Array.from(parent.children).forEach((child, i) => {
        child.setAttribute('data-reveal', 'fade-up');
        child.style.transitionDelay = (i * 90) + 'ms';
        child.style.transitionDuration = '680ms';
        staggerChildren.push(child);
      });
    });

    singles.forEach(el => {
      const dur = el.dataset.duration;
      if (dur === 'slow') el.style.transitionDuration = '950ms';
      else if (dur === 'fast') el.style.transitionDuration = '380ms';
      else el.style.transitionDuration = '700ms';
      if (el.dataset.delay) el.style.transitionDelay = el.dataset.delay + 'ms';
    });

    const allEls = [...singles, ...staggerChildren];

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const el = entry.target;
        const rect = entry.boundingClientRect;
        const vh = window.innerHeight;
        if (entry.isIntersecting) {
          el.classList.remove('rv-out');
          el.style.transitionTimingFunction = 'cubic-bezier(0.22, 1, 0.36, 1)';
          requestAnimationFrame(() => requestAnimationFrame(() => el.classList.add('rv-in')));
        } else {
          const isBelowViewport = rect.top > vh;
          if (scrollDir === 'up' && isBelowViewport) {
            el.classList.remove('rv-in');
            el.classList.add('rv-out');
            el.style.transitionDuration = '250ms';
            el.style.transitionTimingFunction = 'cubic-bezier(0.4,0,1,1)';
          }
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -50px 0px' });

    allEls.forEach(el => observer.observe(el));
    return () => { observer.disconnect(); window.removeEventListener('scroll', updateDir); };
  }, [dep]);
}

/* ─────────────────────────────────────────────
   COMPONENTS
───────────────────────────────────────────── */


function PortfolioHero() {
  return (
    <section className="portfolio-hero">
      <div className="portfolio-hero-in">
        <div className="portfolio-hero-eyebrow">Our Work</div>
        <h1 className="portfolio-hero-h1">
          Projects That<br />
          <em>Define</em> Brands
        </h1>
        <p className="portfolio-hero-sub">
          From immersive VR simulations to AI-powered platforms — a curated look at the work we've delivered for industry leaders across India and beyond.
        </p>
        <div className="portfolio-hero-stats">
          <div>
            <div className="pf-stat-num">50<span>+</span></div>
            <div className="pf-stat-lbl">Projects Delivered</div>
          </div>
          <div>
            <div className="pf-stat-num">30<span>+</span></div>
            <div className="pf-stat-lbl">Enterprise Clients</div>
          </div>
          <div>
            <div className="pf-stat-num">8<span>+</span></div>
            <div className="pf-stat-lbl">Industries Served</div>
          </div>
          <div>
            <div className="pf-stat-num">10<span>/10</span></div>
            <div className="pf-stat-lbl">Client Rating</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ProjectCard({ project, index }) {
  const delay = (index % 2) * 120;
  return (
    <div
      className={`pf-card${project.featured ? ' featured' : ''}`}
      data-reveal="fade-up"
      style={{ transitionDelay: delay + 'ms', transitionDuration: '700ms' }}
    >
      <div className="pf-card-img-wrap">
        <img src={project.img} alt={project.title} className="pf-card-img" />
        <div className="pf-card-overlay">
          <button className="pf-overlay-cta">
            View Case Study
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </button>
        </div>
      </div>
      <div className="pf-card-body">
        <div>
          <span className={`pf-card-tag ${project.tagClass}`}>{project.tag}</span>
          <h3 className="pf-card-title">{project.title}</h3>
          <p className="pf-card-desc">{project.desc}</p>
        </div>
        <div className="pf-card-footer">
          <div className="pf-card-client">
            <div className="pf-client-dot">{project.initials[0]}</div>
            <div className="pf-client-name">{project.client}</div>
          </div>
          <button className="pf-card-link">
            Case Study
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </button>
        </div>
      </div>
    </div>
  );
}

function Portfolio() {
  const [activeCat, setActiveCat] = useState('All');
  const filtered = activeCat === 'All'
    ? PROJECTS
    : PROJECTS.filter(p => p.cats.includes(activeCat));

  // Re-run scroll reveal when filter changes
  useScrollReveal(activeCat);

  return (
    <>
      {/* Sticky filter bar */}
      <div className="pf-filter-wrap">
        <div className="pf-filter-in">
          <div className="pf-filters">
            {CATS.map(cat => (
              <button
                key={cat}
                className={`pf-filter-btn${activeCat === cat ? ' on' : ''}`}
                onClick={() => setActiveCat(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className="pf-count">
            Showing <strong>{filtered.length}</strong> project{filtered.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Grid */}
      <section className="pf-grid-sec">
        <div className="pf-grid-in">
          <div className="pf-grid">
            {filtered.length === 0 ? (
              <div className="pf-empty">
                <div className="pf-empty-ico">🔍</div>
                <div className="pf-empty-h">No projects found</div>
                <div className="pf-empty-p">Try selecting a different category above.</div>
              </div>
            ) : (
              filtered.map((p, i) => (
                <ProjectCard key={p.id} project={p} index={i} />
              ))
            )}
          </div>
        </div>
      </section>
    </>
  );
}

function CTABand() {
  return (
    <section className="pf-cta">
      <div className="pf-cta-in">
        <div data-reveal="fade-down" className="pf-cta-ey">Start a Project</div>
        <h2 data-reveal="fade-up" data-duration="slow" className="pf-cta-h2">
          Ready to Build<br /><em>Something Great?</em>
        </h2>
        <p data-reveal="fade-up" data-delay="100" className="pf-cta-p">
          Let's talk about your idea. Our team is ready to design, build, and ship technology that moves the needle for your business.
        </p>
        <div data-reveal="fade-up" data-delay="200" className="pf-cta-btns">
          <button className="pf-btn-fill">Request a Demo</button>
          <button className="pf-btn-ghost">View Services →</button>
        </div>
      </div>
    </section>
  );
}



/* ─────────────────────────────────────────────
   ROOT
───────────────────────────────────────────── */
export default function VizinfyPortfolio() {
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = STYLES;
    document.head.insertBefore(style, document.head.firstChild);
  }, []);

  return (
    <>
      <PortfolioHero />
      <Portfolio />
      <CTABand />
    </>
  );
}